import React, { FC, useContext, useEffect, useState } from 'react';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { ActionOrderInput } from 'models/order-input/action-order';
import { ActionOrderContext } from './action-order-context';
import ActionOrder from './action-order.component';
import { OrderInputType } from 'models/order-input/shared';
import { LinkedClinic, LinkedUser, Order, PatientCategory } from 'models/order';
import { useLazyQuery } from '@apollo/client';
import { getActionSetTemplate } from 'apollo/templates/action-sets';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Box, CircularProgress } from '@mui/material';
import { useLocation, useParams } from 'react-router';
import { formatPinNumber, isValidPinNumber, requiresSwedishPinValidation } from 'utils/personal-identity-number';
import { orderForActionModifyTemplate } from 'apollo/templates/order/query.graphql';
import { restorationsToActionConstructions } from 'components/order-flow/shared/restoration-input-helper';
import { getClinicTemplate, getOrganizationByExtIdTemplate } from 'apollo/templates/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { Organization } from 'models/organization';
import { useTranslation } from 'react-i18next';
import { ConstructionProvider } from 'context/construction-context';
import { getTreatingClinicianTitleWords, isTreatingClinicianTitle } from 'utils/treating-clinician';

const CreateActionOrder: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const currentUser = currentUserVar();
    const currentOrg = currentOrgVar();
    const defaultClinic = !currentUser.systemSetting?.clinic?.isNotPickUpAndDeliveryPlace
        ? currentUser.systemSetting?.clinic
        : undefined;

    const [inputOrder, setInputOrder] = useState<ActionOrderInput>({} as ActionOrderInput);
    const [isActionSetLoaded, setIsActionSetLoaded] = useState(false);
    const [areConstructionsHydrated, setAreConstructionsHydrated] = useState(false);
    const loading = !isActionSetLoaded || !areConstructionsHydrated;
    const { setOrder } = useContext(ActionOrderContext);
    const params = useParams<{ id: string }>();
    const { showBoundary } = useDenthubErrorBoundary();
    const search = useLocation().search;
    const orderPrefill = new URLSearchParams(search).get('orderPrefill');
    const orderRef = React.useRef<Order | null>(null);
    const initialOrderRef = React.useRef<ActionOrderInput | null>(null);

    const [fetchOrder] = useLazyQuery(orderForActionModifyTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: async ({ order }: { order: Order }) => {
            if (order) {
                if (order.type !== 'ACTION_BASED') {
                    throw new Error('Order is not an action based order');
                }
                orderRef.current = order;
                await getActionSet({
                    variables: {
                        actionSetId: order.actionSet || currentOrg.activeActionSet,
                        orgId: order.customer._id,
                        ...(order.isWarrantyOrder && order.supplier?._id && { labId: order.supplier._id }),
                    },
                });
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [getOrganizationByExtId] = useLazyQuery(getOrganizationByExtIdTemplate);

    const [getClinic] = useLazyQuery(getClinicTemplate);

    const [getActionSet] = useLazyQuery(getActionSetTemplate, {
        onCompleted: ({ actionSet }) => {
            const fetchedOrder = orderRef.current;
            // Warranty orders load a lab-filtered action set (the lab's enabled actions) to drive the
            // picker. Hydrating existing constructions from that filtered set would drop actions the
            // lab has since disabled and crash the lab-delivery step, so for warranty we defer
            // hydration and pull it from the full set (getFullActionSet) below.
            const isWarrantyHydration = !!(fetchedOrder?.isWarrantyOrder && fetchedOrder.supplier?._id);
            const initialOrder: ActionOrderInput = {
                inputType: fetchedOrder?.isWarrantyOrder ? OrderInputType.WARRANTY : OrderInputType.NEW,
                _id: fetchedOrder?._id,
                isDraft: fetchedOrder?.isDraft || false,
                customer: fetchedOrder?.customer?._id ? fetchedOrder?.customer : currentOrg,
                dentist: fetchedOrder?.dentist || dentistBasedOnTitle(),
                supplier: fetchedOrder?.supplier || undefined,
                participants: {
                    linkedDentists: fetchedOrder?.linkedDentists || ([] as LinkedUser[]),
                    linkedClinics: fetchedOrder?.linkedClinics || ([] as LinkedClinic[]),
                    customParticipantEmails: fetchedOrder?.customParticipantEmails || ([] as string[]),
                },
                constructions:
                    fetchedOrder?.restorations?.length && !isWarrantyHydration
                        ? restorationsToActionConstructions(fetchedOrder, actionSet)
                        : [],
                comment: fetchedOrder?.additionalInfo || '',
                pricedRestorations: fetchedOrder?.restorations || [],
                currency: fetchedOrder?.currency || '',
                totalPrice: 0,
                totalVat: 0,
                historicalComments: fetchedOrder?.historicalComments || [],
                patient: {
                    personalIdentityNumber: fetchedOrder?.patient?.personalIdentityNumber
                        ? formatPinNumber(fetchedOrder?.patient.personalIdentityNumber)
                        : '',
                    firstName: fetchedOrder?.patient?.firstName || '',
                    lastName: fetchedOrder?.patient?.lastName || '',
                    noPersonalIdentityNumber: fetchedOrder?.patient?.noPersonalIdentityNumber || false,
                    patientCategory: fetchedOrder?.patient?.category
                        ? {
                              name: fetchedOrder?.patient.category,
                          }
                        : { name: '' },
                },
                files: fetchedOrder?.filesUrl || [],
                sample: fetchedOrder?.deliveryDetails?.sample || { type: '', content: [], otherContent: '' },
                deliveryDetails: {
                    date: fetchedOrder?.deliveryDetails?.date
                        ? new Date(fetchedOrder.deliveryDetails.date)
                        : new Date(),
                    time: {
                        hour: fetchedOrder?.deliveryDetails?.time?.hour || undefined,
                        minute: fetchedOrder?.deliveryDetails?.time?.minute || undefined,
                    },
                    isPickUpAtClinic: fetchedOrder?.deliveryDetails?.isPickUpAtClinic || undefined,
                    pickUpLocation: fetchedOrder?.deliveryDetails?.pickUpLocation?._id
                        ? fetchedOrder?.deliveryDetails?.pickUpLocation
                        : defaultClinic,
                    destination: fetchedOrder?.deliveryDetails?.destination?._id
                        ? fetchedOrder?.deliveryDetails?.destination
                        : defaultClinic,
                },
                labExceptions: fetchedOrder?.deliveryDetails?.reason || {},
                currentActionSet: actionSet,
                attachedScannerCaseFiles: fetchedOrder?.attachedScannerCaseFiles || [],
                vendorReference: fetchedOrder?.vendorReference,
            } as ActionOrderInput;
            initialOrderRef.current = initialOrder;
            setInputOrder(initialOrder);
            setOrder(initialOrder);
            setIsActionSetLoaded(true);
            if (isWarrantyHydration && fetchedOrder?.restorations?.length) {
                getFullActionSet({
                    variables: {
                        actionSetId: fetchedOrder.actionSet || currentOrg.activeActionSet,
                        orgId: fetchedOrder.customer._id,
                    },
                });
            } else {
                setAreConstructionsHydrated(true);
            }
        },
        onError: (error) => {
            showBoundary(error);
        },
    });

    // Full (non-lab-filtered) action set, used only to hydrate a warranty order's existing
    // constructions so actions the lab has since disabled in its price list still resolve.
    const [getFullActionSet] = useLazyQuery(getActionSetTemplate, {
        onCompleted: ({ actionSet }) => {
            const fetchedOrder = orderRef.current;
            const baseOrder = initialOrderRef.current;
            if (!baseOrder) {
                setAreConstructionsHydrated(true);
                return;
            }
            const hydratedOrder = {
                ...baseOrder,
                constructions: fetchedOrder ? restorationsToActionConstructions(fetchedOrder, actionSet) : [],
            } as ActionOrderInput;
            setInputOrder(hydratedOrder);
            setOrder(hydratedOrder);
            setAreConstructionsHydrated(true);
        },
        onError: (error) => {
            showBoundary(error);
        },
    });

    const getPatientCategoryByPin = (pin: string, patientCategories: PatientCategory[]) => {
        const presentYear = new Date().getFullYear();
        const yearFromPin = parseInt(pin.substring(0, 4));
        const age = presentYear - yearFromPin;
        const patientCategory = patientCategories.find((item) => {
            return age >= (item.minage || 0) && age <= (item.maxage || 1000);
        });
        return patientCategory;
    };

    const preFillOrder = async () => {
        const isPatientCategoryEnabled = customerSettings.getCustomerSetting(
            currentOrg.customerSettings,
            CONSTANTS.SETTINGS_NAMES.PATIENT_CATEGORY,
        );
        const patientCategories: PatientCategory[] | undefined = customerSettings.getCustomerSetting(
            currentOrg.customerSettings,
            CONSTANTS.SETTINGS_NAMES.PATIENT_CATEGORIES,
        );
        const isPrefillEnabled = customerSettings.getCustomerSetting(
            currentOrg.customerSettings,
            CONSTANTS.SETTINGS_NAMES.ORDER_PREFILL,
        );
        const clinicExtId = parseInt(
            customerSettings.getCustomerSetting(
                currentOrg.customerSettings,
                CONSTANTS.SETTINGS_NAMES.ORDER_PREFILL_CLINIC_EXT_ID,
            ),
            10,
        );

        if (orderPrefill && isPrefillEnabled) {
            const decoded = atob(orderPrefill);
            const params = Object.fromEntries(new URLSearchParams(decoded));
            const requiresSwedishPin = requiresSwedishPinValidation(currentOrg);
            const formattedPin =
                requiresSwedishPin && params.personal_number
                    ? formatPinNumber(params.personal_number)
                    : params.personal_number || '';
            let patientCategory: PatientCategory | undefined;
            if (requiresSwedishPin && formattedPin) {
                patientCategory = getPatientCategoryByPin(formattedPin, patientCategories || []);
            } else if (isPatientCategoryEnabled && params.patient_category && patientCategories?.length) {
                patientCategory = patientCategories.find(
                    (cat: PatientCategory) => cat.name === params?.patient_category,
                );
            }
            if (patientCategory) {
                patientCategory.translatedName = translate(`patient:${patientCategory.name}`);
            }
            const patientInfo = {
                personalIdentityNumber: formattedPin,
                noPersonalIdentityNumber: requiresSwedishPin ? !isValidPinNumber(formattedPin) : !formattedPin,
                firstName: params.first_name ? params.first_name.replace(/\t/g, ' ').trim() : '',
                lastName: params.last_name ? params.last_name.replace(/\t/g, ' ').trim() : '',
                category: patientCategory?.name,
            };
            const extId = params['clinic_extid_' + clinicExtId];

            let clinic: Organization | null = null;
            if (clinicExtId && clinicExtId !== 3 && extId) {
                const { data } = await getOrganizationByExtId({
                    variables: {
                        extIdField: `externalId${clinicExtId}`,
                        extIdValue: extId,
                    },
                });
                clinic = data?.organizationByExtId;
            } else if (params.clinic_extid_3) {
                const { data } = await getClinic({
                    variables: {
                        clinicId: params.clinic_extid_3,
                    },
                });
                clinic = data?.clinic;
            }

            orderRef.current = {
                ...orderRef.current,
                vendorReference: params.vendorReference || undefined,
                patient: {
                    ...orderRef.current?.patient,
                    ...patientInfo,
                },
                deliveryDetails: {
                    ...orderRef.current?.deliveryDetails,
                    pickUpLocation: clinic ? clinic : orderRef.current?.deliveryDetails.pickUpLocation,
                    destination: clinic ? clinic : orderRef.current?.deliveryDetails.destination,
                },
            } as Order;
        }
    };

    const startFreshOrder = async () => {
        if (orderPrefill) {
            await preFillOrder();
        }
        getActionSet({
            variables: {
                actionSetId: currentOrg.activeActionSet,
                orgId: currentOrg._id,
            },
        });
    };

    useEffect(() => {
        setInputOrder({} as ActionOrderInput);
        setOrder({} as ActionOrderInput);
        orderRef.current = null;
        initialOrderRef.current = null;
        setIsActionSetLoaded(false);
        setAreConstructionsHydrated(false);
        if (params.id) {
            fetchOrder({ variables: { orderId: params.id } });
        } else {
            startFreshOrder();
        }
    }, [params.id, orderPrefill]);

    const dentistBasedOnTitle = () => {
        const titleWords = getTreatingClinicianTitleWords(currentOrg.customerSettings || []);
        return isTreatingClinicianTitle(currentUser.title, titleWords) ? currentUser : undefined;
    };

    return (
        <ActionOrderContext.Provider value={{ order: inputOrder, setOrder: setInputOrder }}>
            <ConstructionProvider>
                {loading || !inputOrder.inputType ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                        <Box>
                            <CircularProgress size={80} />
                        </Box>
                    </Box>
                ) : (
                    <ActionOrder />
                )}
            </ConstructionProvider>
        </ActionOrderContext.Provider>
    );
};

export default CreateActionOrder;
